/**
 * Comprehensive error code mappings from backend WwpErrors.cs
 * This file contains all error codes and their user-friendly messages
 */

export const ERROR_CODES = {
  // Generic Errors (GEN)
  GEN_100: 'Model is invalid',
  GEN_800: 'Feature is not implemented',
  GEN_900: 'Unauthorized access',
  GEN_998: 'An error occurred',
  GEN_999: 'An unexpected error occurred',

  // Account Errors (ACC)
  ACC_100: 'Account not found',
  ACC_101: 'The timezone is the same as the user currently has',
  ACC_102: 'Email or password is incorrect',
  ACC_103: 'Account not verified',
  ACC_104: 'Invalid token',
  ACC_105: 'Email verification failed',
  ACC_106: 'Cannot create Healthcare Professional account through register endpoint. Please use an invite code',
  ACC_107: 'Account with the given email already exists',
  ACC_108: 'The provided token is invalid or has expired',
  ACC_109: 'Email address is not in a valid format',
  ACC_110: 'Email address for the user has not been found',

  // Authentication Errors (AUTH)
  AUTH_100: '2FA Invalid',

  // Healthcare Professional Errors (HCP)
  HCP_100: 'Healthcare Professional does not exist',

  // Patient Errors (PTNT)
  PTNT_100: 'Patient not found',
  PTNT_101: 'Patient already registered with Healthcare Professional',
  PTNT_102: 'Healthcare professional is already approved',
  PTNT_103: 'Healthcare professional was not registered before being approved',
  PTNT_104: 'Healthcare professional is already revoked',
  PTNT_105: 'Healthcare professional was not registered before being revoked',
  PTNT_106: 'Patient is not registered with healthcare professional',

  // Patient Compliance Errors (PCOMP)
  PCOMP_100: 'Patient period compliance entry does not exist',
  PCOMP_101: 'The periods are overlapping',
  PCOMP_102: 'Patient compliance card information was not found',

  // Active Custom Test Errors (ACTEST)
  ACTEST_100: 'Active custom test not found',
  ACTEST_101: 'Bad request',
  ACTEST_102: 'File does not exist',
  ACTEST_103: 'File is empty',
  ACTEST_104: 'File already exists on environment',

  // Invite Errors (INVITE)
  INVITE_100: 'Invite not found',
  INVITE_101: 'Code not found',
  INVITE_102: 'Code has already been redeemed',

  // Active Test Errors (ACTTEST)
  ACTTEST_100: 'Active test not found',
  ACTTEST_101: 'Active test with the specified TUG does not exist',
  ACTTEST_102: 'Active test with the specified 6MWT does not exist',
  ACTTEST_103: 'Active test with the specified 10MWT does not exist',
  ACTTEST_104: 'Active test with the specified Balance does not exist',
  ACTTEST_105: 'Active test type is not in range',
  ACTTEST_106: 'Active test data category type is not in range',

  // Integration Errors (INTEG)
  INTEG_100: 'Alert already exists',

  // Settings Errors (SETT)
  SETT_100: 'Setting does not exist',
  SETT_101: 'Setting already exists for user',
  SETT_102: 'Setting does not exist for user',

  // Default fallback
  DEFAULT: 'An error occurred',
} as const;

export type ErrorCode = keyof typeof ERROR_CODES;

/**
 * Get user-friendly error message for a given error code
 */
export function getErrorMessage(code: string): string {
  return ERROR_CODES[code as ErrorCode] || ERROR_CODES.DEFAULT;
}

/**
 * Check if an error code exists in our mappings
 */
export function isKnownErrorCode(code: string): code is ErrorCode {
  return code in ERROR_CODES;
}

